/**
 * Dictation Hooks
 *
 * React Query hooks for voice dictation
 *
 * QUE CAMBIO Y QUE NO
 * ===================
 * El motor. Antes era `webkitSpeechRecognition`, que solo existe en Chrome de
 * Google y manda el audio a sus servidores; ahora el navegador graba con
 * `MediaRecorder` y manda el audio al backend de la propia clinica.
 *
 * Lo que devuelve este hook es lo mismo que devolvia el anterior, campo por
 * campo, para que las pantallas que ya lo pintan no tengan que cambiar.
 *
 * LO UNICO QUE SE NOTA
 * ====================
 * El texto ya no aparece palabra por palabra sino por frases. La API de
 * Google iba soltando texto provisional mientras se hablaba; Whisper escucha
 * un trozo y devuelve la frase entera. Para dictar un informe suele ser
 * mejor, porque acierta mas con vocabulario clinico, pero se ve distinto.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import * as dictationService from './dictation.service'
import { useModulo, usePermiso } from '@/application/auth/auth.context'
import { getErrorMessage } from '@/lib/api'
import type { SpeechState } from '@/domain/dictation/types'

/**
 * Cada cuanto se cierra un trozo de audio y se manda a transcribir.
 *
 * Es el compromiso entre que el texto tarde en aparecer y que el motor tenga
 * contexto suficiente para acertar. Cinco segundos es una frase dictada
 * completa, que es la unidad con la que trabaja bien Whisper.
 */
const SEGMENT_MS = 5000

/**
 * Lo mas que puede durar un trozo aunque el servidor siga ocupado.
 *
 * Sin este techo, esperar a que termine la transcripcion anterior alarga el
 * trozo sin limite: en un servidor mas lento que el habla se vieron trozos de
 * 482 KB que acababan caducando, y entonces el dictado deja de escribir del
 * todo. Es peor que ir con retraso.
 *
 * Con el techo, si el servidor no da abasto el dictado se atrasa pero sigue
 * escribiendo, y el remedio de verdad es un modelo mas pequeno o mas CPU, no
 * acumular audio en el navegador.
 */
const MAX_SEGMENT_MS = 10000

export const dictationKeys = {
  all: ['dictation'] as const,
  availability: () => [...dictationKeys.all, 'availability'] as const,
}

/**
 * ¿Se puede dictar en esta pantalla?
 *
 * La respuesta la da el servidor, que es quien sabe si la IPS tiene el
 * modulo, si el rol puede dictar y si el motor esta levantado. La
 * comprobacion anterior vivia en el navegador y solo miraba si la API
 * existia: por eso se ofrecia un boton que no hacia nada.
 */
export function useDictationAvailable(): boolean {
  // Antes de preguntar, lo que ya se sabe sin preguntar.
  //
  // El token trae los permisos del usuario y los modulos de su IPS. Lanzar la
  // peticion sin mirarlos primero le pide al servidor algo que va a negar, y
  // un 403 en una pantalla que el rol si puede ver es exactamente lo que
  // vigila `03-pantallas-sin-acceso-denegado`: el medico remitente y el
  // tecnologo entran al visor, y ninguno de los dos dicta.
  const puedeDictar = usePermiso('report:dictate')
  const moduloActivo = useModulo('dictado')

  const { data } = useQuery({
    queryKey: dictationKeys.availability(),
    queryFn: () => dictationService.getDictationAvailability(),
    enabled: puedeDictar && moduloActivo,
    // Que el motor no este levantado no es un fallo pasajero: reintentar no lo
    // va a cambiar, y cada reintento le pega al servicio de transcripcion.
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  // Sin respuesta todavia, no se ofrece: mas vale que el boton aparezca un
  // segundo tarde a que aparezca y no funcione.
  if (!data) return false

  return data.available && typeof MediaRecorder !== 'undefined'
}

export interface UseDictationOptions {
  /** Idioma del dictado. Admite 'es-CO'; al servidor viaja como 'es'. */
  lang?: string
  /** Aplicar los comandos de voz (punto, coma, parrafo). Por defecto si. */
  processCommands?: boolean
  /** Se llama con cada frase ya transcrita. */
  onResult?: (transcript: string) => void
  /** Se llama mientras se graba el trozo siguiente. */
  onInterimResult?: (transcript: string) => void
  /** Se llama ante cualquier fallo. */
  onError?: (error: string) => void
}

export interface UseDictationReturn {
  state: SpeechState
  /** La sesion de dictado esta abierta, se este grabando o transcribiendo. */
  isListening: boolean
  /** El microfono esta abierto ahora mismo. */
  grabando: boolean
  /** El microfono esta cerrado y queda audio por transcribir. */
  transcribiendo: boolean
  pausado: boolean
  transcript: string
  finalTranscript: string
  error: string | null
  startListening: () => void
  stopListening: () => void
  toggleListening: () => void
  pausar: () => void
  /** Suelta el microfono y descarta lo dictado sin que llegue al informe. */
  cancelar: () => void
  resetTranscript: () => void
  isSupported: boolean
}

/**
 * El idioma que espera el servidor: dos letras.
 *
 * Las pantallas pasan 'es-CO' desde que se escribieron, y el codigo de pais
 * no le dice nada al motor.
 */
const toIso639 = (lang: string): string => lang.slice(0, 2).toLowerCase()

/**
 * Hook de dictado por voz.
 */
export function useDictation(options: UseDictationOptions = {}): UseDictationReturn {
  const { lang = 'es-CO', processCommands = true } = options

  const [state, setState] = useState<SpeechState>('idle')
  const [transcript, setTranscript] = useState('')
  const [finalTranscript, setFinalTranscript] = useState('')
  const [error, setError] = useState<string | null>(null)

  const isSupported = useDictationAvailable()

  // Las devoluciones de llamada van en refs para que cambiarlas no reinicie
  // la grabacion en curso. Es el mismo recurso que usaba el hook anterior.
  const onResultRef = useRef(options.onResult)
  const onInterimResultRef = useRef(options.onInterimResult)
  const onErrorRef = useRef(options.onError)

  useEffect(() => {
    onResultRef.current = options.onResult
    onInterimResultRef.current = options.onInterimResult
    onErrorRef.current = options.onError
  }, [options.onResult, options.onInterimResult, options.onError])

  const descartarRef = useRef(false)
  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const segmentTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const shouldKeepRecordingRef = useRef(false)

  /**
   * Hay una transcripcion en curso.
   *
   * Existe para no mandar el trozo siguiente antes de que vuelva el anterior.
   * Sin esto, en un servidor que tarda mas de lo que dura un trozo, las
   * peticiones se acumulan hasta agotar el tiempo de espera y el dictado deja
   * de escribir. Medido el 2026-09-16: trozos de 5s contra un motor que
   * tardaba 8s acababan en una cadena de `speech_timeout`.
   */
  const enviandoRef = useRef(false)

  const fail = useCallback((mensaje: string) => {
    shouldKeepRecordingRef.current = false
    setError(mensaje)
    setState('error')
    onErrorRef.current?.(mensaje)
  }, [])

  /**
   * Suelta el microfono.
   *
   * Importante que ocurra siempre: mientras el `MediaStream` viva, el
   * navegador deja encendido el indicador de grabacion y el sistema considera
   * el microfono ocupado.
   */
  const releaseMicrophone = useCallback(() => {
    if (segmentTimerRef.current) {
      clearTimeout(segmentTimerRef.current)
      segmentTimerRef.current = null
    }

    recorderRef.current = null

    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }, [])

  const sendSegment = useCallback(
    async (blob: Blob, mimeType: string) => {
      if (blob.size === 0 || descartarRef.current) return

      enviandoRef.current = true
      try {
        setState('processing')
        const audioBase64 = await dictationService.blobToBase64(blob)

        const resultado = await dictationService.transcribeDictation({
          audioBase64,
          mimeType: dictationService.baseMimeType(mimeType),
          language: toIso639(lang),
          applyCommands: processCommands,
        })

        setTranscript('')

        if (resultado.text) {
          setFinalTranscript((previo) => previo + resultado.text)
          onResultRef.current?.(resultado.text)
        }

        if (shouldKeepRecordingRef.current) setState('listening')
        else setState((previo) => (previo === 'paused' ? 'paused' : 'idle'))
      } catch (err) {
        fail(getErrorMessage(err, 'No se pudo transcribir el dictado'))
        releaseMicrophone()
      } finally {
        enviandoRef.current = false
      }
    },
    [lang, processCommands, fail, releaseMicrophone]
  )

  /**
   * Graba un trozo completo y lo manda.
   *
   * Se abre y se cierra un `MediaRecorder` por trozo en lugar de pedirle
   * trozos periodicos a uno solo. Con webm, los trozos posteriores al primero
   * no llevan la cabecera del contenedor y no se pueden decodificar sueltos:
   * el servidor recibiria audio ilegible a partir del segundo. Grabando un
   * archivo completo cada vez, cada trozo vale por si mismo.
   */
  const recordSegment = useCallback(() => {
    const stream = streamRef.current
    if (!stream || !shouldKeepRecordingRef.current) return

    const mimeType = dictationService.pickRecordingMimeType()
    if (!mimeType) {
      fail('Este navegador no puede grabar audio')
      releaseMicrophone()
      return
    }

    const recorder = new MediaRecorder(stream, { mimeType })
    recorderRef.current = recorder

    const chunks: Blob[] = []
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data)
    }

    recorder.onstop = () => {
      void sendSegment(new Blob(chunks, { type: mimeType }), mimeType)

      // El siguiente trozo arranca en cuanto se cierra el anterior.
      if (shouldKeepRecordingRef.current) recordSegment()
    }

    recorder.onerror = () => {
      fail('Fallo la grabacion de audio')
      releaseMicrophone()
    }

    recorder.start()
    setState('listening')
    onInterimResultRef.current?.('')

    /**
     * Cierra el trozo, pero no mientras el anterior siga transcribiendose.
     *
     * Asi el trozo se alarga solo cuando el servidor va justo de CPU, en vez
     * de acumular peticiones que acaban caducando. Se sigue grabando todo el
     * rato: no se pierde nada de lo que dice el medico, solo llega en frases
     * mas largas.
     */
    const abiertoDesde = Date.now()

    const cerrarCuandoSePueda = () => {
      const seAlargoDemasiado = Date.now() - abiertoDesde >= MAX_SEGMENT_MS

      if (shouldKeepRecordingRef.current && enviandoRef.current && !seAlargoDemasiado) {
        segmentTimerRef.current = setTimeout(cerrarCuandoSePueda, 500)
        return
      }

      if (recorder.state === 'recording') recorder.stop()
    }

    segmentTimerRef.current = setTimeout(cerrarCuandoSePueda, SEGMENT_MS)
  }, [fail, releaseMicrophone, sendSegment])

  const startListening = useCallback(() => {
    if (shouldKeepRecordingRef.current) return

    if (!isSupported) {
      fail('El dictado por voz no esta disponible')
      return
    }

    setError(null)
    descartarRef.current = false
    shouldKeepRecordingRef.current = true

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        // Si se pulso "parar" mientras el navegador pedia permiso, no se
        // empieza a grabar: se suelta el microfono y ya.
        if (!shouldKeepRecordingRef.current) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }

        streamRef.current = stream
        recordSegment()
      })
      .catch(() => {
        fail(
          'No se pudo acceder al microfono. Revise el permiso del navegador y que haya un microfono conectado.'
        )
      })
  }, [isSupported, fail, recordSegment])

  const stopListening = useCallback(() => {
    shouldKeepRecordingRef.current = false

    // Se cierra el trozo en curso en vez de descartarlo: lo ultimo que dijo
    // el medico antes de soltar el boton tambien se transcribe.
    const recorder = recorderRef.current
    if (recorder && recorder.state === 'recording') recorder.stop()

    releaseMicrophone()
    setTranscript('')
    setState((previo) => (previo === 'processing' ? 'processing' : 'idle'))
  }, [releaseMicrophone])

  const toggleListening = useCallback(() => {
    if (shouldKeepRecordingRef.current) stopListening()
    else startListening()
  }, [startListening, stopListening])

  const resetTranscript = useCallback(() => {
    setTranscript('')
    setFinalTranscript('')
  }, [])

  const pausar = useCallback(() => {
    if (!shouldKeepRecordingRef.current) return
    shouldKeepRecordingRef.current = false

    const recorder = recorderRef.current
    if (recorder && recorder.state === 'recording') recorder.stop()

    releaseMicrophone()
    setTranscript('')
    setState('paused')
  }, [releaseMicrophone])

  const cancelar = useCallback(() => {
    shouldKeepRecordingRef.current = false
    descartarRef.current = true

    const recorder = recorderRef.current
    if (recorder && recorder.state === 'recording') recorder.stop()

    releaseMicrophone()
    setTranscript('')
    setState('idle')
  }, [releaseMicrophone])

  // Soltar el microfono si la pantalla se cierra mientras se dicta.
  useEffect(() => {
    return () => {
      shouldKeepRecordingRef.current = false
      releaseMicrophone()
    }
  }, [releaseMicrophone])

  return {
    state,
    isListening: state === 'listening' || state === 'processing',
    grabando: state === 'listening',
    transcribiendo: state === 'processing',
    pausado: state === 'paused',
    transcript,
    finalTranscript,
    error,
    startListening,
    stopListening,
    toggleListening,
    pausar,
    cancelar,
    resetTranscript,
    isSupported,
  }
}
